import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  InputBase,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  AutoAwesomeOutlined,
  EditSquare,
  CloseRounded,
  ArrowForward,
  ContentCopyOutlined,
  Check,
  ArrowUpward,
  MenuBookOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { tokens } from '../theme/tokens';
import AiChangeCard from './AiChangeCard';
import { ConfirmDialog } from './ui/ConfirmDialog';
import {
  Message,
  MessageBlock,
  MessageChange,
  ERROR_TEXT,
  blocksToPlainText,
  toBlocks,
} from './aiAgentModel';
import { AI_SUGGESTIONS } from './aiAgentSuggestions';
import { useHelpCenterEnabled } from '../hooks/useHelpCenterEnabled';

// Helper function to parse inline markdown (bold, italic, code)
const parseInlineMarkdown = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let key = 0;
  
  // Pattern for **bold**
  const boldPattern = /\*\*([^*]+)\*\*/g;
  // Pattern for `code`
  const codePattern = /`([^`]+)`/g;
  
  // Combine all matches with their positions
  const matches: Array<{ type: 'bold' | 'italic' | 'code'; start: number; end: number; content: string }> = [];
  
  let match;
  
  // Find bold matches first
  while ((match = boldPattern.exec(text)) !== null) {
    matches.push({
      type: 'bold',
      start: match.index,
      end: match.index + match[0].length,
      content: match[1],
    });
  }
  
  // Find code matches
  while ((match = codePattern.exec(text)) !== null) {
    matches.push({
      type: 'code',
      start: match.index,
      end: match.index + match[0].length,
      content: match[1],
    });
  }
  
  // Find italic matches (single * not part of **bold**)
  // We'll check character by character to avoid lookbehind
  const italicPattern = /\*([^*]+)\*/g;
  while ((match = italicPattern.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    // Check if this is part of a bold match (starts with **)
    const isBoldStart = start > 0 && text[start - 1] === '*';
    const isBoldEnd = end < text.length && text[end] === '*';
    // Only include if not inside a bold match
    const isInsideBold = matches.some(m => m.type === 'bold' && start >= m.start && end <= m.end);
    if (!isBoldStart && !isBoldEnd && !isInsideBold) {
      matches.push({
        type: 'italic',
        start,
        end,
        content: match[1],
      });
    }
  }
  
  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);
  
  // Build the result
  matches.forEach((match) => {
    // Add text before the match
    if (match.start > currentIndex) {
      parts.push(text.substring(currentIndex, match.start));
    }
    
    // Add the formatted match
    if (match.type === 'bold') {
      parts.push(
        <Typography key={key++} component="span" sx={{ fontWeight: 600 }}>
          {match.content}
        </Typography>
      );
    } else if (match.type === 'italic') {
      parts.push(
        <Typography key={key++} component="span" sx={{ fontStyle: 'italic' }}>
          {match.content}
        </Typography>
      );
    } else if (match.type === 'code') {
      parts.push(
        <Typography
          key={key++}
          component="span"
          sx={{
            fontFamily: tokens.font.mono,
            backgroundColor: tokens.surface.tint,
            padding: '0.1em 0.3em',
            borderRadius: '4px',
            fontSize: '0.9em',
          }}
        >
          {match.content}
        </Typography>
      );
    }
    
    currentIndex = match.end;
  });
  
  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(text.substring(currentIndex));
  }
  
  return parts.length > 0 ? <>{parts}</> : text;
};

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`;

/** The page the user has open, from the shell's nav data (same source as context.currentPage). */
export interface AIAgentPage {
  label: string;
  icon?: React.ReactNode;
}

interface AIAgentSidebarProps {
  open: boolean;
  onClose: () => void;
  page?: AIAgentPage | null;
  /** Who the applied line names ("Applied for Dana"); the signed-in user's display name. */
  userName?: string;
}

/** Groups the changes Mozi proposes in one chat (the audit record carries it). */
const newConversationId = (): string => `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const hoverGhost = { '&:hover': { bgcolor: tokens.surface.tint } };

const AssistantAvatar: React.FC = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: '7px',
      bgcolor: tokens.accent.tint,
      color: tokens.accent.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      mt: '1px',
    }}
  >
    <AutoAwesomeOutlined sx={{ fontSize: 15 }} />
  </Box>
);

const Blocks: React.FC<{ blocks: MessageBlock[] }> = ({ blocks }) => (
  <>
    {blocks.map((block, i) => {
      if (block.t === 'p') {
        return (
          <Box component="p" key={i} sx={{ m: 0 }}>
            {parseInlineMarkdown(block.text)}
          </Box>
        );
      }
      if (block.t === 'ol') {
        return (
          <Box component="ol" key={i} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {block.items.map((item, j) => (
              <Box component="li" key={j} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: `${tokens.radius.chip}px`,
                    bgcolor: tokens.surface.tint,
                    color: tokens.ink.bodyAlt,
                    fontSize: 11.5,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {(block.start ?? 1) + j}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>{parseInlineMarkdown(item)}</Box>
              </Box>
            ))}
          </Box>
        );
      }
      return (
        <Box component="ul" key={i} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {block.items.map((item, j) => (
            <Box component="li" key={j} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tokens.ink.faint, flexShrink: 0, mt: '9px' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>{parseInlineMarkdown(item)}</Box>
            </Box>
          ))}
        </Box>
      );
    })}
  </>
);

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard can be blocked; the button still confirms nothing was lost.
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button
      onClick={handleCopy}
      startIcon={copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopyOutlined sx={{ fontSize: 16 }} />}
      sx={{
        height: 30,
        px: 1,
        minWidth: 0,
        textTransform: 'none',
        fontSize: 12.5,
        fontWeight: 500,
        color: tokens.ink.muted,
        borderRadius: `${tokens.radius.small}px`,
        '&:hover': { bgcolor: tokens.surface.tint, color: tokens.ink.body },
        '& .MuiButton-startIcon': { mr: '6px', ml: 0 },
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
};

const AIAgentSidebar: React.FC<AIAgentSidebarProps> = ({ open, onClose, page, userName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  // One entry per request in flight, keyed by its loading row id. New chat aborts and clears them all,
  // so a reply that lands afterwards finds no entry and is dropped.
  const pendingRef = useRef<Map<string, AbortController>>(new Map());
  const requestSeq = useRef(0);
  const conversationIdRef = useRef<string>(newConversationId());
  // The proposal waiting on a confirm dialog (a change to more than 500 products).
  // `confirming` stays set while the dialog fades out so its text does not change mid-exit.
  const [confirming, setConfirming] = useState<Message | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  // Help Center (flag `help_center`): the in-app way into the customer docs.
  const { enabled: helpCenterEnabled } = useHelpCenterEnabled();

  const busy = messages.some((m) => m.loading);
  const hasMessages = messages.length > 0;

  // Scroll the message area itself; scrollIntoView can move the page behind the drawer.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Prevent body scroll when AI chat is open on mobile
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile

    const html = document.documentElement;
    const body = document.body;

    if (open) {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      (body as any)._scrollY = scrollY;
      (body as any)._scrollX = scrollX;

      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = `-${scrollX}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
    } else {
      const scrollY = (body as any)._scrollY || 0;
      const scrollX = (body as any)._scrollX || 0;

      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';

      delete (body as any)._scrollY;
      delete (body as any)._scrollX;

      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    }
  }, [open, isMobile]);

  /** Ask `/ai/chat`. `addUser` is false when retrying, so the prompt is not shown twice. */
  const ask = useCallback(
    async (prompt: string, addUser: boolean) => {
      requestSeq.current += 1;
      const stamp = `${Date.now()}-${requestSeq.current}`;
      const loadingId = `l-${stamp}`;
      const controller = new AbortController();
      pendingRef.current.set(loadingId, controller);
      // Replace only this request's own loading row, and only if New chat has not dropped it.
      const settle = (reply: Message) => {
        if (!pendingRef.current.delete(loadingId)) return;
        setMessages((prev) => prev.map((m) => (m.id === loadingId ? reply : m)));
      };
      setMessages((prev) => [
        ...prev,
        ...(addUser ? [{ id: `u-${stamp}`, role: 'user' as const, text: prompt }] : []),
        { id: loadingId, role: 'assistant' as const, loading: true },
      ]);
      try {
        const headers = await getAuthHeaders();
        const response = await makeApiCall('/ai/chat', {
          method: 'POST',
          headers,
          signal: controller.signal,
          body: JSON.stringify({
            message: prompt,
            conversationId: conversationIdRef.current,
            context: {
              currentPage: location.pathname,
              timestamp: new Date().toISOString(),
              language: i18n.language || i18n.resolvedLanguage || 'en',
            },
          }),
        });
        if (!response.ok) throw new Error('Failed to get AI response');
        const data = await response.json();
        const text: string = data.response || data.message || '';
        if (!text) throw new Error('Empty AI response');
        // `/ai/chat` answers markdown only today; derive blocks from it. A future
        // structured reply can carry `blocks`, `action` and `change` directly.
        const blocks: MessageBlock[] = Array.isArray(data.blocks) && data.blocks.length ? data.blocks : toBlocks(text);
        const change: MessageChange | undefined = data.change && data.change.id ? data.change : undefined;
        settle({
          id: `a-${stamp}`,
          role: 'assistant',
          text,
          blocks,
          action: data.action || undefined,
          ...(change ? { change, changeStatus: 'proposed' as const } : {}),
        });
      } catch {
        settle({ id: `e-${stamp}`, role: 'assistant', blocks: [{ t: 'p', text: ERROR_TEXT }], error: true, prompt });
      }
    },
    [location.pathname, i18n.language, i18n.resolvedLanguage],
  );

  // Check for context from sessionStorage when sidebar opens
  useEffect(() => {
    if (open) {
      const context = sessionStorage.getItem('ai-context');
      if (context) {
        sessionStorage.removeItem('ai-context');
        if (messages.length === 0) {
          ask(context, true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSend = () => {
    const prompt = input.trim();
    if (!prompt || busy) return;
    setInput('');
    ask(prompt, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (prompt: string) => {
    if (busy) return;
    ask(prompt, true);
  };

  const handleRetry = (failed: Message) => {
    if (busy || !failed.prompt) return;
    setMessages((prev) => prev.filter((m) => m.id !== failed.id));
    ask(failed.prompt, false);
  };

  /** Apply or undo the change on a message: the card shows progress, then the backend's verified answer. */
  const runChange = useCallback(async (message: Message, verb: 'apply' | 'undo') => {
    const change = message.change;
    if (!change) return;
    const patch = (fields: Partial<Message>) =>
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, ...fields } : m)));
    patch({ changeStatus: 'applying', changeError: undefined, changeCanUndo: false });
    try {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/ai/changes/${encodeURIComponent(change.id)}/${verb}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && (data.status === 'applied' || data.status === 'undone')) {
        patch({
          changeStatus: data.status,
          changeVerified: data.verified === true,
          changePrice: typeof data.after?.price === 'number' ? data.after.price : null,
          changeError: undefined,
        });
        return;
      }
      if (response.status === 403 && data.code === 'forbidden') {
        // Nothing ran: the card goes back to what it was, now without the primary button.
        patch({
          changeStatus: message.changeStatus === 'applied' ? 'applied' : 'proposed',
          change: { ...change, canApply: false, whoCan: data.whoCan || change.whoCan },
        });
        return;
      }
      patch({
        changeStatus: 'failed',
        changeVerified: false,
        changeError: data.error || data.message || undefined,
        changeCanUndo: data.canUndo === true,
      });
    } catch {
      patch({ changeStatus: 'failed', changeVerified: false, changeError: undefined, changeCanUndo: message.changeCanUndo === true });
    }
  }, []);

  const handleApply = (message: Message) => {
    if (message.change?.requiresConfirm) {
      setConfirming(message);
      setConfirmOpen(true);
      return;
    }
    runChange(message, 'apply');
  };

  const handleNewChat = () => {
    pendingRef.current.forEach((controller) => controller.abort());
    pendingRef.current.clear();
    conversationIdRef.current = newConversationId();
    setConfirmOpen(false);
    setConfirming(null);
    setMessages([]);
    setInput('');
  };

  // Handle close - on mobile, only close button works (not backdrop)
  const handleDrawerClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (isMobile && reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const sendDisabled = !input.trim() || busy;
  const sendTooltip = busy ? 'Mozi is still answering' : !input.trim() ? 'Type a question first' : 'Send';
  const pageName = page?.label || undefined;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      variant="temporary"
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 400, md: 450 },
          maxWidth: { xs: '100vw', sm: 400, md: 450 },
          // dvh accounts for mobile browser UI (address bar, navigation)
          height: { xs: '100dvh', sm: '100vh' },
          maxHeight: { xs: '100dvh', sm: '100vh' },
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          bgcolor: tokens.surface.card,
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: { xs: 0, sm: 'auto' },
          zIndex: theme.zIndex.drawer + 1,
          overflow: 'hidden',
          boxSizing: 'border-box',
          '@supports not (height: 100dvh)': {
            height: { xs: '100vh', sm: '100vh' },
            maxHeight: { xs: '100vh', sm: '100vh' },
          },
        },
      }}
      ModalProps={{
        keepMounted: false,
        sx: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: { xs: 'none', sm: 'auto' },
          },
        },
        disableEnforceFocus: false,
        disableAutoFocus: true,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 56,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          pl: 2,
          pr: 1,
          borderBottom: `1px solid ${tokens.line.default}`,
          bgcolor: tokens.surface.card,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: `${tokens.radius.small}px`,
            bgcolor: tokens.accent.tint,
            color: tokens.accent.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeOutlined sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: 14.5, fontWeight: 700, color: tokens.ink.primary, lineHeight: 1.2 }}>
            Mozi
          </Typography>
          <Typography component="div" sx={{ fontSize: 12, color: tokens.ink.muted, lineHeight: 1.3 }}>
            Motzu assistant
          </Typography>
        </Box>
        {hasMessages && (
          <Button
            onClick={handleNewChat}
            startIcon={<EditSquare sx={{ fontSize: 18 }} />}
            sx={{
              height: 34,
              px: '10px',
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 600,
              color: tokens.ink.bodyAlt,
              borderRadius: `${tokens.radius.small}px`,
              ...hoverGhost,
            }}
          >
            New chat
          </Button>
        )}
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ width: 34, height: 34, borderRadius: `${tokens.radius.small}px`, color: tokens.ink.bodyAlt, ...hoverGhost }}
        >
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Message area */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: tokens.surface.card,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {!hasMessages ? (
          <Box sx={{ p: '28px 20px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box>
              <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600, color: tokens.ink.primary, m: 0 }}>
                What can Mozi help with?
              </Typography>
              <Typography component="p" sx={{ fontSize: 14, lineHeight: 1.55, color: tokens.ink.bodyAlt, m: 0, mt: '6px' }}>
                Ask about setup, integrations, the API or anything on this page. Answers take the page you're on into account.
              </Typography>
            </Box>
            <Box>
              <Typography
                component="div"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: tokens.ink.muted,
                  mb: '8px',
                }}
              >
                Suggested
              </Typography>
              <Box sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.card}px`, overflow: 'hidden' }}>
                {AI_SUGGESTIONS.map((s, i) => (
                  <Box
                    key={s.key}
                    component="button"
                    type="button"
                    onClick={() => handleSuggestion(typeof s.prompt === 'function' ? s.prompt(pageName) : s.prompt)}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      p: '12px 14px',
                      m: 0,
                      border: 0,
                      borderTop: i === 0 ? 0 : `1px solid ${tokens.line.hairline}`,
                      bgcolor: tokens.surface.card,
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: tokens.surface.sunken },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: `${tokens.radius.small}px`,
                        bgcolor: tokens.surface.tint,
                        color: tokens.ink.bodyAlt,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: 14, fontWeight: 500, color: tokens.ink.primary }}>{s.label}</Box>
                      <Box sx={{ fontSize: 12, color: tokens.ink.muted }}>
                        {typeof s.hint === 'function' ? s.hint(pageName) : s.hint}
                      </Box>
                    </Box>
                    <ArrowForward sx={{ fontSize: 18, color: tokens.ink.faint, flexShrink: 0 }} />
                  </Box>
                ))}
              </Box>
            </Box>
            {helpCenterEnabled && (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/help');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  p: '12px 14px',
                  m: 0,
                  width: '100%',
                  border: `1px solid ${tokens.line.default}`,
                  borderRadius: `${tokens.radius.card}px`,
                  bgcolor: tokens.surface.card,
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: tokens.surface.sunken },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: `${tokens.radius.small}px`,
                    bgcolor: tokens.surface.tint,
                    color: tokens.ink.bodyAlt,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    '& svg': { fontSize: 18 },
                  }}
                >
                  <MenuBookOutlined />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontSize: 14, fontWeight: 500, color: tokens.ink.primary }}>{t('helpCenter.moziLink.label')}</Box>
                  <Box sx={{ fontSize: 12, color: tokens.ink.muted }}>{t('helpCenter.moziLink.hint')}</Box>
                </Box>
                <ArrowForward sx={{ fontSize: 18, color: tokens.ink.faint, flexShrink: 0 }} />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ p: '20px 20px 8px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {messages.map((message) => {
              if (message.role === 'user') {
                return (
                  <Box key={message.id} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box
                      sx={{
                        maxWidth: '85%',
                        bgcolor: tokens.accent.tint,
                        border: `1px solid ${tokens.accent.border}`,
                        borderRadius: '12px 12px 4px 12px',
                        p: '9px 12px',
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: tokens.ink.primary,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.text}
                    </Box>
                  </Box>
                );
              }
              if (message.loading) {
                return (
                  <Box key={message.id} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <AssistantAvatar />
                    <Box sx={{ height: 24, display: 'flex', alignItems: 'center', gap: '8px' }} role="status" aria-label="Thinking">
                      <Box sx={{ display: 'flex', gap: '4px' }}>
                        {[0, 1, 2].map((n) => (
                          <Box
                            key={n}
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              bgcolor: tokens.accent.main,
                              animation: `${pulse} 1.2s infinite`,
                              animationDelay: `${n * 0.15}s`,
                            }}
                          />
                        ))}
                      </Box>
                      <Box sx={{ fontSize: 13, color: tokens.ink.muted }}>Thinking</Box>
                    </Box>
                  </Box>
                );
              }
              const blocks = message.blocks || [];
              return (
                <Box key={message.id} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <AssistantAvatar />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: tokens.ink.body,
                      wordBreak: 'break-word',
                    }}
                  >
                    <Blocks blocks={blocks} />
                    {message.change && (
                      <AiChangeCard
                        change={message.change}
                        status={message.changeStatus || 'proposed'}
                        error={message.changeError}
                        canUndo={message.changeCanUndo}
                        verified={message.changeVerified}
                        price={message.changePrice}
                        userName={userName}
                        onApply={() => handleApply(message)}
                        onUndo={() => runChange(message, 'undo')}
                        onSecondary={() => {
                          navigate(message.change!.secondary.href);
                          onClose();
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {message.error ? (
                        <Button
                          onClick={() => handleRetry(message)}
                          sx={{
                            height: 30,
                            px: 1,
                            minWidth: 0,
                            textTransform: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            color: tokens.accent.hover,
                            borderRadius: `${tokens.radius.small}px`,
                            '&:hover': { bgcolor: tokens.accent.tint },
                          }}
                        >
                          Try again
                        </Button>
                      ) : (
                        <>
                          {message.action && (
                            <Button
                              onClick={() => {
                                navigate(message.action!.href);
                                onClose();
                              }}
                              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 30,
                                px: '10px',
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 600,
                                color: tokens.accent.hover,
                                border: `1px solid ${tokens.line.strong}`,
                                borderRadius: `${tokens.radius.small}px`,
                                '&:hover': { bgcolor: tokens.accent.tint, borderColor: tokens.accent.border },
                              }}
                            >
                              {message.action.label}
                            </Button>
                          )}
                          {/* Raw reply text keeps line breaks and continuations (a multiline curl) intact. */}
                          <CopyButton text={message.text || blocksToPlainText(blocks)} />
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title={
          confirming?.change?.payload.productCount !== undefined
            ? `Apply this change to ${confirming.change.payload.productCount.toLocaleString('en-US')} products?`
            : 'Apply this change?'
        }
        message="The next sync writes it to every product in your selection. Undo restores the previous setting."
        confirmLabel="Apply change"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          if (confirming) runChange(confirming, 'apply');
        }}
      />

      {/* Composer */}
      <Box
        sx={{
          flexShrink: 0,
          p: '12px 16px 14px',
          pb: { xs: 'calc(14px + env(safe-area-inset-bottom, 0px))', sm: '14px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
          boxSizing: 'border-box',
          bgcolor: tokens.surface.card,
        }}
      >
        <Box
          sx={{
            border: `1px solid ${tokens.line.strong}`,
            borderRadius: `${tokens.radius.card}px`,
            bgcolor: tokens.surface.card,
            boxShadow: tokens.shadow.card,
            '&:focus-within': { borderColor: tokens.accent.main, boxShadow: `0 0 0 2px ${tokens.accent.main}` },
          }}
        >
          <InputBase
            multiline
            fullWidth
            minRows={1}
            placeholder="Ask Mozi about this page or the platform"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{ 'aria-label': 'Message Mozi' }}
            sx={{
              p: '12px 14px 6px',
              fontSize: 14,
              lineHeight: 1.5,
              color: tokens.ink.primary,
              '& textarea': { maxHeight: 132, overflowY: 'auto !important' },
              '& textarea::placeholder': { color: tokens.ink.faint, opacity: 1 },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: '6px 8px 8px 10px' }}>
            {page ? (
              <Tooltip title="Mozi sees which page you're on">
                <Box
                  sx={{
                    height: 24,
                    px: 1,
                    minWidth: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    borderRadius: `${tokens.radius.chip}px`,
                    bgcolor: tokens.surface.tint,
                    color: tokens.ink.bodyAlt,
                    fontSize: 12,
                    fontWeight: 500,
                    '& svg': { fontSize: 14 },
                  }}
                >
                  {page.icon}
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {page.label}
                  </Box>
                </Box>
              </Tooltip>
            ) : (
              <span />
            )}
            <Tooltip title={sendTooltip}>
              <span>
                <IconButton
                  onClick={handleSend}
                  disabled={sendDisabled}
                  aria-label="Send message"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: `${tokens.radius.small}px`,
                    bgcolor: tokens.accent.main,
                    color: tokens.ink.inverse,
                    '&:hover': { bgcolor: tokens.accent.hover },
                    '&.Mui-disabled': { bgcolor: tokens.surface.tint, color: tokens.ink.faint },
                  }}
                >
                  <ArrowUpward sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Typography component="div" sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
          Enter to send · Shift + Enter for a new line
        </Typography>
      </Box>
    </Drawer>
  );
};

export default AIAgentSidebar;
